import { findDay } from "./taskDetails";
/**
 * A request to make a Notice, Event or Poll, however it's typed: "mk a poll",
 * "snd a notis 4 me", "Now an EVENT! Sports Day", "event. staff meeting next
 * wednesday 14:30 in the library", "Good morning. I need to send an urgent notice
 * to Grade 3 parents about…". The matching model hears slang, capitals and a story
 * around the request as some other question, so this is read before it.
 *
 * Pure: the message in; what to start, and the request as the task should read it, out.
 */
import { looksLikeQuestion } from "./sideQuestion";
import { isWritingRequest } from "./writingRequest";

export type WorkKind = "notice" | "event" | "poll";

export type WorkRequest =
  | {
      kind: WorkKind;
      /** The request, as the task reads its details: the sentence that asks, from the verb on */
      request: string;
      /** What was said after the request ("Parents should bring…"): a Notice's or Event's message */
      message?: string;
      /** A question or a wording request said with it ("Can you help me word it?"), answered alongside */
      aside?: string;
      /** "the notice", "my event": the one already open, not a new one */
      meansTheOpenOne: boolean;
      /** The message starts with the request (after "ok", "now" and the like), rather than mentioning it part-way */
      atStart: boolean;
      /** Which sentence of the message the request is in: 0 for the first */
      sentence: number;
      /** "make the science fair event": what it's called, said between the verb and the kind */
      title?: string;
      /** "a notice about the bus route, an event for the science fair, and a poll…": the ones after this one */
      thenAlso?: WorkKind[];
    }
  /** A Notice with a picture: something only the wizard can make */
  | { kind: "image"; request: string };

/** Text-speak for the words a request is made of. */
const TEXT_SPEAK: Array<[RegExp, string]> = [
  [/\bsnd\b/gi, "send"],
  [/\bmsg\b/gi, "message"],
  [/\bgrade(?=\d)/gi, "grade "],
  [/\bmk\b/gi, "make"],
  [/\b(?:ntc|notis|notise|noice|notic|otice|noitce|notcie|noticce|notiec|notce|noticee)\b/gi, "notice"],
  [/\b(?:evnt|evnet|evant|evet|eveny|eventt|evebt)\b/gi, "event"],
  [/\b(?:pol|pole|poel|pll)\b(?!\s+(?:vault|dance|dancing|position))/gi, "poll"],
  [/\b(?:sned|sedn|snd)\b/gi, "send"],
  [/\bu\b/gi, "you"],
  [/\b(?:pls|plz)\b/gi, "please"],
  // "grade 4 parents" keeps its number: only a 4 that isn't a grade, year or class is "for"
  [/(?<!\b(?:grade|gr|year|yr|class|room|std|standard|form|level)\s*)\b4\s+(?=me\b|us\b|parents\b|the\b|everyone\b|everybody\b|evry1\b|(?:test\s+)?(?:grade|gr)\b|jumpstart\b)/gi, "for "],
  [/\b2\s+(?=evry1\b|every1\b|everyone\b|everybody\b|all parents\b|the\b|(?:test\s+)?grade\b|jumpstart\b)/gi, "to "],
  [/\b(?:evry1|every1)\b/gi, "everyone"],
  [/\bwit\b/gi, "with"],
  [/\bw\s+(?=(?:a|the)\s+)/gi, "with "],
  [/\babt\b/gi, "about"],
  [/\bsprts\b/gi, "sports"],
  [/\bmak\b/gi, "make"],
  [/\b(?:parnts|prnts|parens)\b/gi, "parents"],
];

export function withoutTextSpeak(text: string): string {
  return TEXT_SPEAK.reduce((said, [pattern, word]) => said.replace(pattern, word), text);
}

const NOUN = "(notice|announcement|event|poll)s?";
/** Words a request can have between its verb and what it makes: "an urgent", "a quick", "the science fair". */
const ARTICLE = "an?|the|my|our|this|that|another|one|new";
const DESCRIBING = "quick|short|normal|simple|fun|info(?:rmation)?|emergency|urgent|image|picture|pic|photo|img";
const VERB =
  "(?:put|add|turn|make|set)\\s+(?:that|this|it)(?:\\s+(?:in|up|down))?(?:\\s+(?:as|into))?|create|make|send|post|publish|start|run|draft|write|do|set\\s+up|plan|prepare|organi[sz]e|add|need|want|like|get|put\\s+together|put\\s+out";
/** "put that in as an event": the request points back at what was said before it. */
const BACK_REFERENCE = /\b(?:that|this|it)\b/i;
/** "Now, the bake sale itself.": a sentence that only names something. */
const NAMED_ON_ITS_OWN = /^(?:the|our|a|an)\s+((?:[a-z0-9'’-]+\s+){0,3}?[a-z0-9'’-]+)(?:\s+itself)?[.!]*$/i;
/** "the trip poll — options are Durban, Drakensberg, Cape Town": a Poll, named by what it's about, with its choices. */
const POLL_WITH_CHOICES =
  /^(?:(?:the|a|an|our|my)\s+)?((?:[a-z0-9'’-]+\s+){0,3}?)poll\s*[—–:,-]+\s*(?:the\s+)?(?:options|choices)\s*(?:are\b|:|=)\s*(.+)$/i;

/** Words that end a request's noun phrase: "need to know when the event is" isn't asking for an Event. */
const NOT_DESCRIBING = "to|when|if|is|are|was|what|how|why|where|whether|know|see|check|find|tell|for|with|on|in|at|about|it|from|of";

/** "make a poll", "send an urgent notice", "make the science fair event": the verb, then what it makes. */
const VERB_THEN_NOUN = new RegExp(
  `\\b(${VERB})\\s+(?:(?:me|us)\\s+)?(?:(${ARTICLE})\\s+)?((?:(?!(?:${NOT_DESCRIBING})\\b)[a-z0-9'’-]+\\s+){0,3}?)${NOUN}\\b`,
  "i"
);
const DESCRIBING_ONLY = new RegExp(`^(?:(?:${DESCRIBING}|another|new|one|a|an)\\s+)*$`, "i");

/** "a notice about the bus route change, an event for the science fair…, and a poll…": where the next request starts. */
const ANOTHER_REQUEST = new RegExp(`,?\\s+(?:and\\s+)?(?:then\\s+)?(?:an?|the)\\s+(?:[a-z-]+\\s+){0,2}?${NOUN}\\b`, "gi");

/** "Now an EVENT!", "POLL TIME", "event. staff meeting…", "Notice: Bus route 3…": what it makes, said first. */
const NOUN_FIRST = new RegExp(
  `^(?:(${ARTICLE})\\s+)?(?:(?:${DESCRIBING})\\s+)*${NOUN}(?:\\s+time)?(?=$|[\\s.!:,;–—-])(?!\\s+(?:done|is|was|sent|went|has|have|results?|options?|question|already|from|out|isn'?t|aren'?t|wasn'?t|won'?t|doesn'?t|didn'?t|not|never|missing|disappeared|vanished|gone|still)\\b)`,
  "i"
);

/** Openers that say nothing about the request: "ok slay.", "bet.", "BOOM!!! Now…", "and also…". */
const OPENER =
  /^(?:(?:now|ok(?:ay)?|bet|boom|right|next|and|also|then|fine|great|lovely|ooh|oh|so|yes|yeah|yep|ja|eish|sorry|please|just|well|cool|slay|alright|hey|hi|hello|darling|hayibo|shoh|ag|nah|bestie|lowkey|no cap|again|forget it|forget that|none of those|perfect|thanks|thank you|hang on|hold on|wait)\b[\s,.!:—–-]*)+/i;

/** "park the bus notice, make the science fair event first": setting one aside before asking for the next. */
const SET_ASIDE_FIRST = /^(?:park|leave|pause|forget|skip|drop|hold)\s+(?:the|my|that)\s+[^,]+,\s*/i;

/** "make sure…", "do you…": a verb that isn't asking for something to be made. */
const NOT_A_REQUEST = /^(?:make\s+sure|do\s+(?:you|i))\b/i;

/** Asks about something rather than for it: "When is the sports day notice?", "Did my notice go out?". */
const ASKS_ABOUT =
  /^(?:how|where|why|what|whats|which|who|whos|when|is|are|does|did|was|were|has|have|should|will|would|can|could)\b|^do\s+(?!an?\b|the\b)/i;

/** "did it go out?", "has the notice been sent?": about something already sent. */
const ABOUT_SENT = /\b(?:did|has|have)\b[^?]*\b(?:go(?:ne)?|went|sent|out|published|arrive[d]?)\b/i;

/** Never a fresh request: a reminder for, or a copy of, something sent. */
const NOT_NEW = /\b(?:reminder|remind|duplicate|copy of|resend)\b/i;
/** "new event Teddy Bear Picnic … with a reminder the day before": a new one, whose reminder is one of its details. */
const MAKES_A_NEW_ONE =
  /^(?:(?:please|can\s+you|could\s+you|i\s+need\s+to|i\s+want\s+to|hi|hey|ok)[,\s]+)*(?:new|create|make|add|set\s+up|plan|put\s+together)\s+(?:an?\s+|the\s+|a\s+new\s+)?(?:[\w'’-]+\s+){0,4}?(?:event|poll)\b/i;
/** Looking for one already made: "Locate an existing poll", "find the poll I sent". Never a new one. */
const FINDS_ONE = /^(?:please\s+)?(?:find|locate|look\s+up|search\s+for|show(?:\s+me)?|where(?:'s|\s+is|\s+are)?)\b[^.?!]*\b(?:existing|sent|old|previous|last|already)\b/i;

/** A picture said with the request: "an image notice", "a notice with a pic", "type image". */
/** A picture to put on it; never "Photo day" or "photo forms", which are what a Notice is about. */
const PICTURE = /\b(?:image|picture|pic|pics|photo|photos|img|foto)\b(?!\s+(?:day|days|shoot|session|sessions|money|forms?|orders?|consent))/i;

/** A sentence, with where it starts in the message. */
type Sentence = { text: string; start: number };

function sentencesOf(text: string): Sentence[] {
  return Array.from(text.matchAll(/[^.!?]+(?:[.!?]+|$)/g))
    .map((found) => ({ text: found[0], start: found.index ?? 0 }))
    .filter((sentence) => sentence.text.trim());
}

const article = (word: string | undefined) => (word ? word.toLowerCase() : "");
const capitalized = (words: string) => words.charAt(0).toUpperCase() + words.slice(1);
const kindOf = (noun: string): WorkKind =>
  noun.toLowerCase().startsWith("poll") ? "poll" : noun.toLowerCase().startsWith("event") ? "event" : "notice";
/** "the notice", "my event": the one being talked about, not a new one. */
const meansOne = (word: string) => ["the", "my", "our", "this", "that"].includes(word);

/** The sentences after the request: a message, or a question asked alongside it. */
type SaidAfter = { message?: string; aside?: string; to?: string };
const NOTHING_AFTER: SaidAfter = {};

/** "create a notice saying X." with "to Grade 4" said last: "create a notice to Grade 4 saying X." */
const toAfterTheNoun = (request: string, to: string) => request.replace(new RegExp(`\\b${NOUN}\\b`, "i"), (noun) => `${noun} ${to}`);

function after(sentences: Sentence[], from: number): SaidAfter {
  const rest = sentences
    .slice(from)
    .map((s) => s.text.trim())
    .join(" ")
    .trim();
  if (!rest) return {};
  // "… saying Please check. to TEST Grade 4 2027": who it goes to, said last, isn't the message
  if (/^(?:to|for)\s+\S/i.test(rest) && rest.split(/\s+/).length <= 6) return { to: rest.replace(/[.!]+$/, "") };
  const labelledMessage = rest.match(/^(?:message|body|text)\s*:\s*([\s\S]+)$/i);
  if (labelledMessage) return { message: labelledMessage[1] };
  return looksLikeQuestion(rest) || isWritingRequest(rest) ? { aside: rest } : { message: rest };
}

/** "So I want to ask the parents: Which treat should we bake most of?": a Poll, without the word. */
const ASK_THE_PARENTS = /\b(?:want|like|need|going)\s+to\s+ask\s+(?:the\s+|all\s+the\s+|our\s+)?(?:parents|families|everyone|staff)\s*[:,]\s*(.+\?[^?]*)$/i;

/** "ask parents which fundraiser they prefer: cake sale, raffle or fun run": the question, then its choices. */
const ASK_THE_PARENTS_WHICH =
  /\b(?:want|like|need|going)\s+to\s+ask\s+(?:the\s+|all\s+the\s+|our\s+)?(?:parents|families|everyone|staff)\s+(which|what|whether|if)\s+([^:?]+?)\s*[:?]\s*(.+?)[.?!]*$/i;

/** "… anonymous?", "… private?": what a Poll or Notice can be, asked about. */
const WHAT_IT_CAN_BE = /^\s*(?:anonymous|private|secret|editable|public|multiple[\s-]choice|close|closed|longer|shorter|bigger)\b/i;

/** The request this message makes, if it makes one. */
/** "just one parent", "a single family", "only one person": who it goes to is the question. */
export const ONE_PERSON = /\b(?:(?:just|only)\s+(?:one|a\s+single|a)|a\s+single|one|an\s+individual|a\s+specific)\s+(?:parent|person|family|user|learner'?s?\s+parent)\b/i;

/** "I want to send something to Grade 4 parents", "I need to tell all parents about term reports": a Notice, without the word. */
const TELL_SOMEONE =
  /^(?:(?:hi|hello|ok(?:ay)?|so|um+|sorry)[,.!\s]+)*i\s+(?:really\s+)?(?:want|need|would\s+like|'d\s+like|wanna|have)\s+to\s+(?:send\s+(?:something|a\s+(?:message|note|letter))|tell|inform|let|message|write\s+to)\s+(.+)$/i;
/** "send grade 4 parents a note about the gala", "can you send the jumpstart class a quick message". */
const SEND_THEM_A_NOTE =
  /^(?:(?:i\s+(?:really\s+)?(?:want|need|would\s+like|'d\s+like|wanna)\s+to|please|can\s+you|could\s+you)\s+)?send\s+(?!an?\b|the\s+(?:note|message)\b|it\b|this\b|that\b)(.{2,40}?)\s+an?\s+(?:quick\s+|short\s+)?(?:note|message|letter|update|notice)\b\s*((?:about|on|saying|re|regarding|that)\b.*)?$/i;
/** "whole school notice about thank you", "grade 4 notice about the trip": who it's for, then the kind. */
const AUDIENCE_THEN_NOUN =
  /^(?:an?\s+)?((?:the\s+)?whole[\s-]school|every(?:one|body)|all[\s-](?:the\s+)?parents|(?:test\s+)?grade\s*\w+(?:\s+\d{4})?|gr\s*\d+|class)\s+(notice|event|poll)\b\s*(.*)$/i;
/** "about something", "about a thing": no topic yet. */
const NO_TOPIC = /\s*\b(?:about|re|regarding)\s+(?:something|a\s+thing|stuff|anything)\b/i;
/** "I'd like to ask parents will your child be at the gala, Yes or No": a Poll's question, then maybe its choices. */
const ASK_THE_PARENTS_ANY =
  /\b(?:want|like|need|going)\s+to\s+ask\s+(?:the\s+|all\s+(?:the\s+)?|our\s+)?(?:parents|families|everyone|staff)\s*[:,]?\s+((?:should|will|would|do|does|can|could|is|are|which|what|when|where|who|how|if|whether)\b.+)$/i;
/** "can you add it?", "please put it on the calendar": what was just described, as an Event. */
const ADD_IT =
  /^(?:(?:can|could|would|will)\s+you\s+(?:please\s+)?|please\s+)?(?:add|put|pop|stick)\s+(?:it|this|that)(?:\s+(?:in|on|to|into)\s+(?:the\s+|our\s+)?(?:calendar|diary))?(?:\s+for\s+me)?(?:\s+please)?[?.!]*$/i;
/** A day or a time said: what makes a description an Event. */
const SAYS_WHEN =
  /\b(?:today|tomorrow|tonight|mon|tues?|wed(?:nes)?|thu(?:rs)?|fri|sat(?:ur)?|sun)(?:day)?\b|\b\d{1,2}(?::\d{2})?\s*(?:am|pm)\b|\b\d{1,2}\s+(?:jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*\b/i;

/** A concrete Event described without asking to create it yet. Retained for one explicit follow-up. */
export function describedEventRequest(said: string): string | null {
  const text = withoutTextSpeak(said.trim()).replace(/^(?:so|just so you know)[,\s]+/i, "");
  if (looksLikeQuestion(text) || ASKS_ABOUT.test(text) || /^(?:please|send|remind|nudge|notify|tell|post|show|find|open)\b/i.test(text) || !findDay(text)) return null;
  const having = text.match(/^(?:we'?re|we are)\s+having\s+(?:(?:a|an|the)\s+)?(.+)$/i);
  const happening = text.match(/^(?:(?:the|our)\s+)?(.{3,70}?)\s+(?:is happening|will be|is)\s+(.+)$/i);
  const details = having?.[1] ?? (happening ? `${happening[1]} ${happening[2]}` : null);
  return details ? `create an event called ${details.replace(/\bat\s+from\b/i, "from")}` : null;
}

export const asksToAddDescribedEvent = (said: string) => ADD_IT.test(withoutTextSpeak(said.trim()));

/** "I need to run a quick vote with parents", "ask the parents a question and get their votes": a Poll, without the word. */
const RUN_A_VOTE =
  /^(?:(?:hi|ok(?:ay)?|so|sorry)[,.!\s]+)*(?:i\s+(?:need|want|would\s+like|'d\s+like|have)\s+to\s+|(?:can|could)\s+(?:you|we|i)\s+|let'?s\s+|please\s+)(?:(?:run|do|hold|have|make|create|start|set\s+up|send(?:\s+out)?)\s+(?:a|an)\s+(?:quick\s+|short\s+)?(?:vote|survey|questionnaire)\b|ask\s+(?:the\s+|all\s+(?:the\s+)?)?(?:parents|families)\s+(?:a\s+question\s+)?(?:and|to)\s+(?:get|collect|see)\s+(?:their\s+)?(?:votes?|answers?)\b)/i;
/** "I need to put something on the calendar": an Event, without the word. */
const ON_THE_CALENDAR =
  /^(?:(?:hi|ok(?:ay)?|so|sorry)[,.!\s]+)*(?:i\s+(?:need|want|would\s+like|'d\s+like|have)\s+to|(?:can|could)\s+(?:you|we|i))\s+(?:put|add|get)\s+(?:something|a\s+thing|a\s+date|an\s+outing|a\s+trip)\s+(?:on|in|into|onto)\s+(?:the\s+|our\s+)?(?:calendar|diary)\b/i;
/** "How do I send a notice? Can you do it with me?": the one just named, made together. */
const DO_IT_WITH_ME =
  /^(?:(?:can|could|would|will)\s+you\s+(?:please\s+)?(?:do|make|send|start|set\s+up|help\s+me\s+(?:do|with))\s+(?:it|one|that|this)(?:\s+(?:with|for)\s+me)?|(?:can|could|shall)\s+we\s+do\s+it\s+together|(?:please\s+)?(?:do|make)\s+it\s+(?:with|for)\s+me|walk\s+me\s+through\s+it)[\s?.!]*$/i;
/** "help me write a notice for parents", with nothing to reword yet: one, made together. */
export const HELP_ME_MAKE_ONE =
  /^(?:(?:please|(?:can|could)\s+you(?:\s+please)?)\s+)?help\s+me\s+(?:write|make|create|send|do|draft|put\s+together)\s+(?:a|an)\s+(?:new\s+|quick\s+|short\s+)?(notice|announcement|poll|event)\b(?:\s+(?:for|to)\s+[^.?!:]{1,40})?[\s?.!]*$/i;

/** "I want to invite parents to something", "invite the families to the maths evening": an Event, not a new User. */
const INVITE_TO_SOMETHING =
  /\binvite\s+(?:the\s+|all\s+(?:the\s+)?|my\s+|our\s+)?(?:parents|families|everyone|grade\s*\w+\s+parents)\s+to\s+(?!(?:the\s+|my\s+|our\s+)?(?:app|noticeboard|circle|class|group|team|platform|account)\b)(.+?)[\s?.!]*$/i;

/** Words that say who it's for, never what it's called: "a grade 4 notice", "a whole school event". */
const SAYS_WHO = /\b(?:grade|gr|class|school|parents?|families|everyone|staff|team|all|my|our|jumpstart)\b/i;

/** "will your child be at the gala, Yes or No" as "Will your child be at the gala? Yes or No". */
function questionThenChoices(asked: string): string {
  const said = asked.trim().replace(/[.!]+$/, "");
  const mark = said.indexOf("?");
  const comma = said.indexOf(",");
  const [question, choices] =
    mark >= 0 ? [said.slice(0, mark), said.slice(mark + 1)] : comma >= 0 ? [said.slice(0, comma), said.slice(comma + 1)] : [said, ""];
  const q = question.trim();
  return `${q.charAt(0).toUpperCase()}${q.slice(1)}?${choices.trim() ? ` ${choices.trim()}` : ""}`;
}

/** "Title: Snack rota.", "Message: Please check…": a label, then what goes in that part of a Notice. */
const TITLE_LABEL = /\b(?:title|heading|subject)(?:\s*[:=]\s*|\s+(?=[^:]+[,;]\s*(?:message|body|text)\s*:))/i;
const MESSAGE_LABEL = /[.,;]?\s*\b(?:(?:the\s+)?message|text|body|it\s+says|saying)\s*[:=]\s*/i;
/** "a notice called "Stationery top-up"", "a notice titled Science project due": its title, said with the request. */
const CALLED = new RegExp(
  `\\b(?:notice|announcement)s?\\s+(?:called|titled|named|entitled)\\s+(?:["“'‘]([^"”'’]+)["”'’]|(.+?))(?=\\s+(?:to|for|saying|with\\s+(?:the\\s+)?message|that\\s+says|about)\\b|\\s*,?\\s*(?:the\\s+)?(?:message|text|body|question)\\s*[:=]|\\s*[:;]|[.!?]\\s|[.!?]?$)`,
  "i"
);
/** "… in the homework book. To: Everyone": who it goes to, said after the message, taken off it. */
export function audienceAtTheEnd(message: string | undefined): { message?: string; to?: string } {
  const toLast = message?.match(/[.!?]\s+(?:send\s+it\s+)?(to|for|audience)\s*:?\s+([^.!?:]{1,60})[.!?]*$/i);
  if (!message || toLast?.index === undefined) return message ? { message } : {};
  return { message: message.slice(0, toLast.index + 1).trim(), to: `${toLast[1].toLowerCase() === "audience" ? "to" : toLast[1].toLowerCase()} ${toLast[2].trim()}` };
}
const unquoted = (text: string) => text.trim().replace(/^["“'‘]+|["”'’.!]+$/g, "").trim();

/**
 * A Notice asked for with its parts labelled: "New notice for Grade 4. Title: X. Message: Y", or
 * "send a notice called "X" to the grade 4 parents: Y". The title and message, and the request without them.
 */
function labelledParts(text: string): { rest: string; title?: string; message?: string } | null {
  const titled = text.match(TITLE_LABEL);
  if (titled?.index !== undefined) {
    const after = text.slice(titled.index + titled[0].length);
    const labelled = after.match(MESSAGE_LABEL);
    const title = unquoted(labelled?.index !== undefined ? after.slice(0, labelled.index) : after);
    const said = labelled?.index !== undefined ? after.slice(labelled.index + labelled[0].length).trim() : undefined;
    const { message, to } = audienceAtTheEnd(said);
    const rest = `${text.slice(0, titled.index).trim().replace(/[.,;:]+$/, "")}${to ? ` ${to}` : ""}`;
    return title && rest ? { rest, title, ...(message ? { message } : {}) } : null;
  }
  const called = text.match(CALLED);
  if (called?.index === undefined) return null;
  const title = unquoted(called[1] ?? called[2] ?? "");
  if (!title) return null;
  const start = called.index + called[0].search(/\s(?:called|titled|named|entitled)\s/i);
  const before = text.slice(0, start).trim();
  let after = text.slice(called.index + called[0].length).trim();
  // "… to the grade 4 parents: Please check…": who it's for stays with the request, the rest is the message
  let message: string | undefined;
  const colon = after.indexOf(":");
  const withMessage = after.match(/^with\s+(?:the\s+)?message\s+["“](.+?)["”]\s*(.*)$/i);
  const saying = after.match(/^(?:saying|that\s+says|which\s+says)\s+/i);
  if (withMessage) {
    message = withMessage[1];
    after = withMessage[2];
  } else if (saying) {
    message = after.slice(saying[0].length).trim();
    after = "";
  } else if (colon >= 0 && after.slice(0, colon).split(/\s+/).length <= 7) {
    message = after.slice(colon + 1).trim();
    // ", message: …": the label is not part of the request
    after = after.slice(0, colon).trim().replace(/^,?\s*(?:the\s+)?(?:message|text|body)$/i, "");
  }
  // "… pencil case. to TEST Grade 4 2027": who it goes to, said after the message
  const last = audienceAtTheEnd(message);
  if (last.to) {
    after = `${after} ${last.to}`.trim();
    message = last.message;
  }
  return { rest: `${before} ${after}`.trim(), title, ...(message ? { message } : {}) };
}

/** Explicit field labels, including the compact forms staff paste into chat. */
function suppliedFields(text: string): Record<string, string> {
  const labels = Array.from(text.matchAll(/\b(title|heading|subject|message|body|text|who|audience|to|for|question|q|choices|options|answers)\s*[:=–—-]\s*|\bsend to\s*:\s*|^send to\s+/gim));
  const fields: Record<string, string> = {};
  for (let i = 0; i < labels.length; i++) {
    const match = labels[i];
    const value = text.slice((match.index ?? 0) + match[0].length, labels[i+1]?.index ?? text.length).trim().replace(/\s*\|\s*$/, "");
    const key = (match[1] ?? "to").toLowerCase();
    fields[key] = value;
  }
  return fields;
}
function prefixAudience(request: string): string | undefined {
  const last = Array.from(request.matchAll(/\b(?:to|for)\s+/gi)).pop();
  if (last?.index === undefined) return undefined;
  const value = request.slice(last.index + last[0].length).split(/\s+(?:today|tomorrow|tonight|on|at|by)\b|[.!?]/i)[0].trim();
  return value && !/\b(?:notice|event|poll|send|write|get|make)\b/i.test(value) ? value : undefined;
}
/** A supplied Notice in header form, or with an explicitly quoted title and body. */
function noticeEnvelope(text: string): WorkRequest | null {
  const saying=text.match(/^(.*?\bnotice\b.*?)\s+(?:saying|it should say:)\s+([\s\S]+)$/i);
  if(saying) {
    const called=saying[1].match(/\b(?:titled|called|named)\s+(.+?)(?=\s+(?:to|for)\b|$)/i);
    const request=(called?saying[1].replace(called[0], ""):saying[1]).trim();
    const tail=audienceAtTheEnd(saying[2]);
    return {kind:"notice",request:`${request}${tail.to?` ${tail.to}`:""}`,...(called?{title:called[1]}:{}),message:tail.message,meansTheOpenOne:false,atStart:true,sentence:0};
  }
  const unlabelled=text.match(/^(.*?)\btitle\s+(.+?),\s*message\s+([\s\S]+)$/i);
  if (unlabelled && /\bnotice\b/i.test(unlabelled[1])) return {kind:"notice",request:unlabelled[1].replace(/[, ]+$/, ""),title:unlabelled[2],message:unlabelled[3],meansTheOpenOne:false,atStart:true,sentence:0};
  const fields = suppliedFields(text);
  const title = fields.title ?? fields.heading ?? fields.subject;
  const message = fields.message ?? fields.body ?? fields.text;
  const audience = fields.audience ?? fields.who ?? fields.to;
  const urgentPrefix=/\burgent notice\b/i.test(text.split(/\b(?:title|heading|subject|message|body|text)\s*[:=]/i)[0]);
  if (title && message && audience) return { kind: "notice", request: `${/^Create a notice\b/.test(text) ? "Create" : "create"} ${urgentPrefix ? "an urgent" : "a"} notice to ${audience}`, title: title.replace(/[.]+$/, ""), message, meansTheOpenOne: false, atStart: true, sentence: 0 };
  const paragraphs = text.trim().split(/\n\s*\n/);
  const heading = paragraphs[0]?.match(/^(?:a\s+)?notice\s+(?:for|to)\s+(.+)$/i)?.[1]?.replace(/\s+(?:please|pls|plz)[.!]*$/i, "");
  if (heading && paragraphs.length >= 3) return { kind: "notice", request: `create a notice to ${heading}`, title: paragraphs[1].trim(), message: paragraphs.slice(2).join("\n\n").trim(), meansTheOpenOne: false, atStart: true, sentence: 0 };
  const urgent = text.match(/^urgent\s*:\s*(.+?)[.!]\s+(.+?)\s+(?:to|for)\s+(.+?)[.!]*$/is);
  if (urgent) return { kind: "notice", request: `create an urgent notice to ${urgent[3]}`, title: urgent[1], message: urgent[2], meansTheOpenOne: false, atStart: true, sentence: 0 };
  const lines = text.trim().split(/\r?\n/);
  if (lines.length >= 3) {
    const headers = lines.slice(0, 2).map((line) => line.match(/^(To|Subject|Title)\s*:\s*(.+)$/i));
    const audience = headers.find((header) => header?.[1].toLowerCase() === "to")?.[2];
    const title = headers.find((header) => /^(?:subject|title)$/i.test(header?.[1] ?? ""))?.[2];
    const message = lines.slice(2).join("\n").trim().replace(/^(?:message|body|text)\s*:\s*/i, "");
    if (audience && title && message) return { kind: "notice", request: `create a notice to ${audience}`, title, message, meansTheOpenOne: false, atStart: true, sentence: 0 };
  }
  const quotedParts = text.match(/\b(?:the\s+)?title\s+(?:should\s+be|is)\s+["“'‘](.+?)["”'’]\s+(?:and\s+)?(?:the\s+)?message\s+(?:(?:should\s+be|is)\s+)?["“'‘](.+?)["”'’][.!]*(?:\s+(?:thanks|thank you|please)[.!]*)?$/is);
  if (quotedParts?.index !== undefined) {
    const request = text.slice(0, quotedParts.index).trim();
    if (/\b(?:notice|announcement)\b/i.test(request)) return { kind: "notice", request: prefixAudience(request) ? `create a notice to ${prefixAudience(request)}` : request, title: quotedParts[1], message: quotedParts[2], meansTheOpenOne: false, atStart: true, sentence: 0 };
  }
  return null;
}

/** Explicit Event requests whose details are spread across clauses. */
function eventEnvelope(text: string): WorkRequest | null {
  const make = (request: string, title?: string): WorkRequest => ({kind:"event", request, ...(title ? {title} : {}), meansTheOpenOne:false, atStart:true, sentence:0});
  const holding=text.match(/\b(?:we're|we are) holding (.+?)\.\s*it starts(?: at)? (.+?) and will be in (.+?)\.\s*(?:can|could) you set up an event[?.!]*$/i);
  if(holding)return make(`create an event called ${holding[1]} at ${holding[2].replace(/^from /i,"from ")} in ${holding[3]}`);
  const staff=text.match(/^(?:new\s+)?event[, ]+((?:just the staff|staff only|teachers only))\s*:\s*(.+)$/i);
  if(staff)return make(`create an event called ${staff[2]} for internal team only`);
  const addAfter=text.match(/^(.+?),\s*(?:can|could) you (?:add|put|make) (?:(?:it|this|that) as an? |an? )event[?.!]*$/i);
  if(addAfter)return make(`create an event called ${addAfter[1]}`);
  const audienceAfter=text.match(/^(.+?\bevent\b.+?)[?.!]\s*it'?s(?: for|,)? ((?:staff only|teachers only|internal team only)|[^.!?]+)[.!?]*(?:\s*(?:thank you(?: so much)?|thanks)[.!?]*)?$/i);
  if(audienceAfter) {
    const title=audienceAfter[1].match(/(?:create|make|add)\s+(?:the\s+|an?\s+)?(.+?)\s+event\b/i)?.[1];
    return make(`${audienceAfter[1]} for ${audienceAfter[2]}`,title);
  }
  return null;
}

export function readWorkRequest(said: string): WorkRequest | null {
  const text = withoutTextSpeak(said.trim()).replace(/\b4 (?=test\s+(?:grade|jumpstart)\b)/gi,"for ").replace(/(\d)\.(\d{2})\s*(am|pm)\b/gi,"$1:$2$3").replace(/^(?:new\s+)?calendar\s+event\b/i, "create an event").replace(/,\s*not\s+(?:remind(?:ing)?\b|(?:a|an|the)\s+(?:existing\s+)?reminder\b).*$/i, "");
  if (/^(?:i (?:can'?t|cannot|can not) find|where is|where'?s)\b.*\b(?:add|new|create) event button\b/i.test(text)) return null;
  const audiencePoll = text.match(/^(?:for\s+)?(.+?)(?:,\s*(?:a\s+)?poll\s*[:—–-]|\s+poll\s*[-:—–])\s*(.+)$/i);
  if (audiencePoll && /^(?:(?:the |my |our )?(?:grade|gr)\s*\d|jumpstart|everyone)/i.test(audiencePoll[1])) return {kind:"poll",request:`create a poll to ${audiencePoll[1]}: ${audiencePoll[2]}`,meansTheOpenOne:false,atStart:true,sentence:0};
  const quickVote = text.match(/^(?:quick\s+)?(?:vote|survey)(?:\s+(?:please|pls))?\s*[:—–-]?\s*(.*)$/i);
  if (quickVote) return {kind:"poll",request:`create a poll${quickVote[1] ? `: ${quickVote[1]}` : ""}`,meansTheOpenOne:false,atStart:true,sentence:0};
  if (/^i want to ask parents something[?.!]*$/i.test(text)) return {kind:"poll",request:"create a poll",meansTheOpenOne:false,atStart:true,sentence:0};
  const voteAbout = text.match(/^can we vote on (.+)$/i);
  if (voteAbout) return {kind:"poll",request:`create a poll about ${voteAbout[1]}`,meansTheOpenOne:false,atStart:true,sentence:0};
  const askAudience = text.match(/^(?:i want to )?ask\s+([^:]+):\s*((?:which|what|when|where|who|why|how|should|would|can|will|is|are|do)\b.+)$/i) ?? text.match(/^(?:i want to )?ask\s+((?:the )?(?:grade|gr)\s*\d+(?: parents)?)\s+((?:which|what|when|where|who|why|how|should|would|can|will|is|are|do)\b.+)$/i);
  if (askAudience) return {kind:"poll",request:`create a poll to ${askAudience[1].replace(/:$/, "")}: ${askAudience[2]}`,meansTheOpenOne:false,atStart:true,sentence:0};
  const suppliedMessage = text.match(/^send (?:a|the) (?:message|note) to (.+?) saying (.+)$/i);
  if (suppliedMessage) return {kind:"notice",request:`create a notice to ${suppliedMessage[1]}`,message:suppliedMessage[2],meansTheOpenOne:false,atStart:true,sentence:0};
  const helpParents = text.match(/^(?:hey\s+)?can you help me send something to (?:the )?parents[?.!]*$/i);
  if (helpParents) return {kind:"notice",request:"create a notice",meansTheOpenOne:false,atStart:true,sentence:0};
  const notifyBody = text.match(/^(?:notify\s+(.+?)\s*:\s*|everyone needs to know\s+)(.+)$/i);
  if (notifyBody) return {kind:"notice",request:`create a notice to ${notifyBody[1] ?? "Everyone"}`,message:notifyBody[2],meansTheOpenOne:false,atStart:true,sentence:0};
  const freshReminder = text.match(/^send\s+(.+?)\s+a reminder that\s+(.+)$/i);
  if (freshReminder) return {kind:"notice",request:`create a notice to ${freshReminder[1]}`,message:freshReminder[2],meansTheOpenOne:false,atStart:true,sentence:0};
  const urgentBodyOnly = text.match(/^urgent:\s*((?:grade|gr)\s*\d+)\s+(.+)$/i);
  if (urgentBodyOnly) return {kind:"notice",request:`create an urgent notice to ${urgentBodyOnly[1]}`,message:urgentBodyOnly[2],meansTheOpenOne:false,atStart:true,sentence:0};
  const urgentNarrative = text.match(/^(.+?),\s*i need to tell (everyone|the whole school) urgently[.!]*$/i);
  if (urgentNarrative) return {kind:"notice",request:`create an urgent notice to ${urgentNarrative[2]}`,message:urgentNarrative[1],meansTheOpenOne:false,atStart:true,sentence:0};
  const dashNotice = text.match(/^(?:notice to\s+)?(.+?(?:parents|class))\s*[-–—]\s*(.+?)\s*[-–—]\s*(.+)$/i);
  if (dashNotice) return {kind:"notice",request:`create a notice to ${dashNotice[1]}`,title:dashNotice[2],message:dashNotice[3],meansTheOpenOne:false,atStart:true,sentence:0};
  const colonDashNotice = text.match(/^notice to (.+?):\s*(.+?)\s+[-–—]\s+(.+)$/i);
  if (colonDashNotice) return {kind:"notice",request:`create a notice to ${colonDashNotice[1]}`,title:colonDashNotice[2],message:colonDashNotice[3],meansTheOpenOne:false,atStart:true,sentence:0};
  const votingAudience = text.match(/^(everyone|(?:the )?(?:grade|gr)\s*\d+(?: parents)?|jumpstart(?: parents)?):\s*vote\s+(.+)$/i);
  if (votingAudience) return {kind:"poll",request:`create a poll to ${votingAudience[1]} about ${votingAudience[2]}`,meansTheOpenOne:false,atStart:true,sentence:0};
  const preference = text.match(/^ask (.+?) if they prefer (.+)$/i);
  if (preference) return {kind:"poll",request:`create a poll to ${preference[1]}: Which do you prefer? Options: ${preference[2]}`,meansTheOpenOne:false,atStart:true,sentence:0};
  const shortEvent = text.match(/^(?:can you |could you |please )?add (.+?) (mon|tues?|wed|thurs?|fri|sat|sun)\s+(.+)$/i);
  if (shortEvent && /\d.*(?:am|pm|:)/i.test(shortEvent[3])) {
    const days:Record<string,string> = {mon:"monday",tue:"tuesday",tues:"tuesday",wed:"wednesday",thu:"thursday",thur:"thursday",thurs:"thursday",fri:"friday",sat:"saturday",sun:"sunday"};
    return {kind:"event",request:`create an event called ${shortEvent[1]} on ${days[shortEvent[2].toLowerCase()]} ${shortEvent[3]}`,meansTheOpenOne:false,atStart:true,sentence:0};
  }
  if (/^poll or claim list\b/i.test(text)) return null;
  const privateNotice=text.match(/^i (?:need|want) to send (.+?) a private notice[?.!]*$/i);
  if(privateNotice)return {kind:"notice",request:`create a notice to ${privateNotice[1]}`,meansTheOpenOne:false,atStart:true,sentence:0};
  const howToMake=text.match(/^how do i (?:send|make|create) a notice (?:about|abt) (.+?)\?\s*can (?:you|u) do it[?.!]*$/i);
  if(howToMake)return {kind:"notice",request:`create a notice about ${howToMake[1]}`,meansTheOpenOne:false,atStart:true,sentence:0};
  const shorthandAudience=text.match(/^(.*?)\b4\s+((?:my\s+)?(?:grade|gr)\s*\w+\s+class)(.*)$/i);
  if(shorthandAudience && /\b(?:notice|poll|event)\b/i.test(shorthandAudience[1])) return readWorkRequest(`${shorthandAudience[1]}for ${shorthandAudience[2]}${shorthandAudience[3]}`);
  const broadcastSentence=text.match(/^i (?:need|want|would like) to tell ((?:the |my |our )?(?:(?:grade|gr)\s*\w+(?: class| parents)?|jumpstart(?: class| parents)?|whole school|everyone|everybody|all parents))\s+([\s\S]+)$/i);
  if(broadcastSentence && !/^about\b/i.test(broadcastSentence[2]))return {kind:"notice",request:`create a notice to ${broadcastSentence[1]}`,message:broadcastSentence[2],meansTheOpenOne:false,atStart:true,sentence:0};
  const urgentBody=text.match(/^(?:send (?:an? )?)?urgent notice (?:to|for) (.+?)\s*:\s*([\s\S]+)$/i);
  if(urgentBody)return {kind:"notice",request:`create an urgent notice to ${urgentBody[1]}`,message:urgentBody[2],meansTheOpenOne:false,atStart:true,sentence:0};
  const askEveryone=text.match(/^(?:i (?:want|need|would like) to )?ask (everyone|everybody|the whole school)\s+([\s\S]+)$/i);
  if(askEveryone && /^(?:which|what|when|where|who|why|how|should|can|will|is|are)\b/i.test(askEveryone[2])) return {kind:"poll",request:`create a poll to ${askEveryone[1]}: ${askEveryone[2]}`,meansTheOpenOne:false,atStart:true,sentence:0};
  const event = eventEnvelope(text);
  if (event) return event;
  const envelope = noticeEnvelope(text);
  if (envelope) return envelope;
  const fields = suppliedFields(text);
  const suppliedQuestion = fields.question ?? fields.q;
  const suppliedChoices = fields.choices ?? fields.options ?? fields.answers;
  const suppliedAudience = fields.audience ?? fields.to ?? fields.for;
  if (suppliedQuestion && suppliedChoices) return { kind: "poll", request: `create a poll${suppliedAudience ? ` to ${suppliedAudience}` : ""}: ${suppliedQuestion} Options: ${suppliedChoices}`, meansTheOpenOne: false, atStart: true, sentence: 0 };
  const commaPoll=text.match(/^(.*?\bpoll\b.*?[:—–-]\s*)((?:which|what|when|where|how|is|are|can|will|would|should)\b[^?]+?),\s*([^?]+)[?]\s*(?:🙏)?$/i);
  if(commaPoll && /,|\sor\s|\sand\s/i.test(commaPoll[3])) return {kind:"poll",request:`${commaPoll[1]}${commaPoll[2]}? Options: ${commaPoll[3]}`,meansTheOpenOne:false,atStart:true,sentence:0};
  const explicitYesNo = text.match(/^(?:(?:make|create|start)\s+(?:a\s+)?)?yes\s*(?:or|\/)\s*no\s+poll\s+(?:to|for)\s+(.+?)\s*[:—–-]\s*(.+)$/i);
  if (explicitYesNo) return { kind: "poll", request: `create a poll to ${explicitYesNo[1]}: ${explicitYesNo[2].replace(/\s+(?:cheers|thanks|thank you|please)[.!]*$/i, "")} Options: Yes, No`, meansTheOpenOne: false, atStart: true, sentence: 0 };
  const quickYesNo = text.match(/^(?:a\s+)?quick\s+poll\s*[:—–-]\s*(.+?\?)\s*\(yes\s*\/\s*no\)\s*[-—–]\s*(.+)$/i);
  if (quickYesNo) return { kind: "poll", request: `create a poll to ${quickYesNo[2].replace(/\s+(?:thanks|thank you|cheers|thx|ta)[.!]*$/i, "")}: ${quickYesNo[1]} Options: Yes, No`, meansTheOpenOne: false, atStart: true, sentence: 0 };
  const indirectBinary = text.match(/^(?:make|create|start)\s+(?:a\s+)?poll\s+asking\s+(?:if|whether)\s+(.+?)[?.]\s*yes\s*(?:or|\/)\s*no[.!]*\s*for\s+(.+)$/i);
  if(indirectBinary)return readWorkRequest(`ask ${indirectBinary[2].replace(/\s+(?:thanks|thank you|cheers|ta)[.!]*$/i, "")} whether ${indirectBinary[1]}?`);
  const binaryText=text.replace(/^(?:i want to|i need to|i would like to|i'd like to)\s+ask\s+/i,"ask ").replace(/^(?:(?:can you|could you|please)\s+)?(?:make|create|start)\s+(?:a\s+)?yes\s*(?:or|\/)\s*no\s+poll\s+asking\s+/i,"ask ");
  const binaryAsk = binaryText.match(/(?:^|[.!?]\s+)(?:(?:can|could)\s+you\s+|please\s+)?ask\s+(.+?)\s+(?:if|whether)\s+(their child|they|you|we)\s+(is|will|can|could|would|should|are|do|have)\s+(.+)$/i);
  if (binaryAsk && !/\b(?:options|choices|answers)\b/i.test(binaryAsk[4])) {
    const subject = binaryAsk[2].toLowerCase() === "their child" ? "your child" : binaryAsk[2].toLowerCase() === "they" ? "you" : binaryAsk[2].toLowerCase();
    const body = binaryAsk[4].split("?")[0].replace(/[,;]\s*(?:just\s+)?yes\s*(?:or|\/)\s*no.*$/i, "").replace(/\s+(?:cheers|thanks|thank you|please)[.!]*$/i, "").replace(/[.!]+$/, "");
    return { kind: "poll", request: `create a poll to ${binaryAsk[1]}: ${capitalized(binaryAsk[3].toLowerCase())} ${subject} ${body}? Options: Yes, No`, meansTheOpenOne: false, atStart: binaryAsk.index === 0, sentence: 0 };
  }
  const broadcast = text.match(/^(?:(?:please|can you|could you)\s+)?(?:let\s+(.+?)\s+know\s+that|remind\s+(.+?)\s+that)\s+(.+)$/i);
  if (broadcast) return { kind: "notice", request: `create a notice to ${broadcast[1] || broadcast[2]}`, message: broadcast[3], meansTheOpenOne: false, atStart: true, sentence: 0 };
  const notify = text.match(/^(?:(?:please|can you|could you)\s+)?notify\s+(.+?)\s+that\s+(.+)$/i);
  if (notify) return { kind: "notice", request: `create a notice to ${notify[1]}`, message: notify[2], meansTheOpenOne: false, atStart: true, sentence: 0 };
  const postSomething = text.match(/^(?:(?:please|can you|could you)\s+)?post\s+something\s+(?:for|to)\s+(.+?)\s+about\s+(.+)$/i);
  if (postSomething) return { kind: "notice", request: `create a notice to ${postSomething[1]} about ${postSomething[2]}`, meansTheOpenOne: false, atStart: true, sentence: 0 };
  const sendPoll = text.match(/^(?:(?:please|can you|could you|i want to|i need to)\s+)?send\s+(.+?)\s+a\s+poll\s*:\s*(.+)$/i);
  if (sendPoll) return { kind: "poll", request: `create a poll to ${sendPoll[1]}: ${sendPoll[2]}`, meansTheOpenOne: false, atStart: true, sentence: 0 };
  const scheduledNotice = text.match(/^(?:(?:i want to|i need to|please|can you|could you)\s+)?schedule\s+(?:a|the|my|a new)\s+notice\b(.*)$/i);
  if (scheduledNotice) {
    const audience = scheduledNotice[1].match(/\bto\s+(.+?)(?=\s+(?:on|at|tomorrow|next|this)\b|$)/i)?.[1];
    return { kind: "notice", request: `create a notice${audience ? ` to ${audience}` : ""}`, meansTheOpenOne: false, atStart: true, sentence: 0 };
  }
  const classMessage = text.match(/^i\s+(?:need|want|would like)\s+to\s+tell\s+((?:the\s+|my\s+|our\s+)?(?:grade|gr)\s*\w+\s+(?:parents|families|class))\s+(?!that\b|about\b)(.+)$/i);
  if (classMessage) return { kind: "notice", request: `create a notice to ${classMessage[1]}`, message: classMessage[2], meansTheOpenOne: false, atStart: true, sentence: 0 };
  const suppliedBroadcast = text.match(/^(?:(?:please|can you|could you)\s+)?(?:let\s+(.+?)\s+know|tell\s+((?:the\s+|my\s+|our\s+)?(?:(?:grade|gr)\s+\w+(?:\s+class)?|jumpstart(?:\s+parents)?|whole school|everyone|all parents)))\s+(?!about\b)(.+)$/i);
  if (suppliedBroadcast) return { kind: "notice", request: `create a notice to ${suppliedBroadcast[1] || suppliedBroadcast[2]}`, message: suppliedBroadcast[3].replace(/^that\s+/i, ""), meansTheOpenOne: false, atStart: true, sentence: 0 };
  const letKnow = text.match(/^(?:(?:please|can you|could you)\s+)?let\s+(.+?)\s+know\s+about\s+(.+)$/i);
  if (letKnow) return { kind: "notice", request: `create a notice to ${letKnow[1]} about ${letKnow[2]}`, meansTheOpenOne: false, atStart: true, sentence: 0 };
  // A compact Event form: a named occasion, date, clock time and venue/audience.
  // Narrative descriptions still ask for an explicit follow-up; questions and Notice content do not start Events.
  const compactEvent = text.replace(/^(?:(?:please|can you|could you)\s+)?(?:add|set up|create|plan|arrange)\s+/i, "");
  const explicitEvent = compactEvent !== text;
  const slashDate = compactEvent.match(/\b\d{1,2}\/\d{1,2}(?:\/\d{4})?\b/)?.[0];
  const compactDay = findDay(compactEvent) ?? (slashDate ? findDay(`on ${slashDate}`) : undefined);
  const eventName = /\b(?:day|evening|night|meeting|picnic|concert|gala|recital|parade|sale|tea|fair|exhibition|training|prize giving|book swap|outing|trip|party|run|talk|visit|bee|function|morning|walk|launch|tournament|assembly|practice)\b/i;
  const clockDetail = /\b(?:\d{1,2}(?::\d{2})?\s*(?:am|pm)|\d{1,2}:\d{2}|half past|quarter (?:past|to)|noon|midday)\b/i;
  if (!/\b(?:notices?|events?|polls?|calendar)\b/i.test(compactEvent) && !looksLikeQuestion(compactEvent) && !ASKS_ABOUT.test(compactEvent) && !/^(?:send|post|publish|tell|notify|remind|find|show|open|we|i|there)\b/i.test(compactEvent) && !/\b(?:is|will be|we are|we're)\b/i.test(compactEvent) && eventName.test(compactEvent.slice(0, 65)) && compactDay && clockDetail.test(compactEvent) && /\b(?:at|in|for|staff only|teachers only)\b/i.test(compactEvent)) {
    return { kind: "event", request: `create an event called ${slashDate && !findDay(compactEvent) ? compactEvent.replace(slashDate, `on ${slashDate}`) : compactEvent}`, meansTheOpenOne: false, atStart: explicitEvent, sentence: 0 };
  }
  if (!text || ABOUT_SENT.test(text) || (NOT_NEW.test(text) && !MAKES_A_NEW_ONE.test(text) && !/^event\s+(?:for|called|named)\b/i.test(text)) || FINDS_ONE.test(text)) return null;
  if (/^(?:title|heading|subject)\s*[:=]/i.test(text) && MESSAGE_LABEL.test(text)) {
    return readWorkRequest(`create a notice. ${text.replace(/\s*\|\s*(?=(?:body|message|text|audience|to|for)\s*:)/gi, ". ")}`);
  }
  const askNamed = text.match(/^(?:i\s+(?:want|need|would like)\s+to|(?:can|could)\s+you|please)\s+ask\s+(.+?)\s+((?:which|what|when|where|who|how|should|will|would|can|could|is|are|do|does)\b.+\?[^?]*)$/i);
  if (askNamed && !/^(?:(?:the|our|all(?: the)?)\s+)?(?:parents|families|everyone|staff)$/i.test(askNamed[1])) return { kind: "poll", request: `create a poll to ${askNamed[1]}: ${askNamed[2]}`, meansTheOpenOne: false, atStart: true, sentence: 0 };
  const childYesNo = text.match(/^(?:(?:can you|could you|please)\s+)?(?:make|create|start)\s+a\s+yes\s*(?:or|\/)\s*no\s+poll\s+asking\s+(.+?)\s+if\s+their\s+child\s+is\s+(.+?)(?:\s+(?:thx|thanks|thank you|please))?[.!?]*$/i);
  if (childYesNo) return { kind: "poll", request: `create a poll to ${childYesNo[1]}: Is your child ${childYesNo[2]}? Options: Yes, No`, meansTheOpenOne: false, atStart: true, sentence: 0 };
  const booleanAsk = text.match(/^(?:(?:please|can you|could you)\s+)?ask\s+(?:the\s+|all\s+the\s+)?(?:parents|families)\s+(?:if|whether)\s+(we|they|you)\s+(should|can|could|will|would|are|do|have)\s+(.+?)[?.!]*$/i);
  if (booleanAsk) {
    const subject = booleanAsk[1].toLowerCase() === "they" ? "you" : booleanAsk[1].toLowerCase();
    const question = `${capitalized(booleanAsk[2].toLowerCase())} ${subject} ${booleanAsk[3]}?`;
    return { kind: "poll", request: `create a poll: ${question} Options: Yes, No`, meansTheOpenOne: false, atStart: true, sentence: 0 };
  }
  const namedOccasion = text.match(/^(?:(?:please|can you|could you)\s+)?(?:set up|plan|organise|organize|arrange)\s+(.{3,70}?)\s+((?:(?:on|next|this|tomorrow|today)\b).+)$/i);
  if (namedOccasion && /\b(?:day|evening|night|drive|walk|talk|meeting|picnic|concert|gala|recital|parade|sale|tea|fair|launch|braai|party|outing|trip|training)\b/i.test(namedOccasion[1]) && !/\b(?:notice|poll|event|circle|user|team|account)\b/i.test(namedOccasion[1]) && SAYS_WHEN.test(namedOccasion[2]) && /\b(?:at|from)\s+(?:\d|half|quarter|noon|midday)/i.test(namedOccasion[2])) {
    return { kind: "event", request: `create an event called ${namedOccasion[1]} ${namedOccasion[2]}`, meansTheOpenOne: false, atStart: true, sentence: 0 };
  }
  const namedAsEvent = text.match(/^(?:(?:please|can you|could you)\s+)?(?:set\s+up|create|make|i\s+need|i\s+want)\s+(.{2,70}?)\s+as\s+an?\s+event\b(.*)$/i);
  if (namedAsEvent) return { kind: "event", request: `create an event called ${namedAsEvent[1]}${namedAsEvent[2]}`, meansTheOpenOne: false, atStart: true, sentence: 0 };
  const namedCalendar = text.match(/^(?:(?:please|can you|could you)\s+)?(?:put|add|pop|stick|enter)\s+(?!(?:it|this|that|something)\b)(.{3,60}?)\s+(?:on(?:to)?|in(?:to)?|to)\s+(?:the|our|my)\s+calendar\b/i);
  if (namedCalendar) return { kind: "event", request: text, meansTheOpenOne: false, atStart: true, sentence: 0 };
  const referring = text.match(/^(.*?)\b(?:could|can|would|will)\s+you\s+(?:please\s+)?(?:make|create|start|set\s+up)\s+one\s*(.+)$/is);
  const mentionedKind = referring && Array.from(referring[1].matchAll(/\b(notice|poll|event)\b/gi)).pop()?.[1];
  if (referring && mentionedKind) {
    const kind = kindOf(mentionedKind);
    return { kind, request: `create ${kind === "event" ? "an" : "a"} ${kind} ${referring[2]}`, meansTheOpenOne: false, atStart: true, sentence: 0 };
  }
  if (/\b(?:i\s+(?:need|want)\s+(?:the\s+)?parents\s+to\s+(?:vote|choose)|(?:need|want)\s+to\s+ask\s+(?:the\s+)?parents\s+to\s+choose|help\s+me\s+(?:run|hold|make)\s+(?:a\s+)?(?:quick\s+)?vote)\b/i.test(text)) {
    return { kind: "poll", request: "create a poll", meansTheOpenOne: false, atStart: true, sentence: 0 };
  }
  const labelledPoll = text.match(/^(?:q|question)\s*[:=]\s*(.+?)\s*(?:answers|options|choices)\s*[:=]\s*(.+)$/is);
  if (labelledPoll) return { kind: "poll", request: `create a poll: ${labelledPoll[1].replace(/[\s.?]+$/, "")}? Options: ${labelledPoll[2]}`, meansTheOpenOne: false, atStart: true, sentence: 0 };
  const telling = text.match(/^(?:(?:please|can you|could you|would you)\s+)?(?:tell|inform)\s+(.+?)\s+(?:that|about)\s+(.+?)(?:\s+(?:thanks|thank you|please))?[.!?]*$/i);
  if (telling) return { kind: "notice", request: `create a notice to ${telling[1]}`, message: telling[2], meansTheOpenOne: false, atStart: true, sentence: 0 };
  const casual = text.match(/^can we draft (?:a )?(.+?) update for (?:our |the )?parents[?.!]*$/i)
    ?? text.match(/^help me tell everyone about (.+?)[?.!]*$/i)
    ?? text.match(/^i want to prepare (?:a )?(.+?) message for (?:the |our )?school[?.!]*$/i);
  if (casual) return { kind: "notice", request: `create a notice about ${casual[1]}`, meansTheOpenOne: false, atStart: true, sentence: 0 };
  // "New notice for Grade 4. Title: X. Message: Y": read without the labelled parts, which are kept as they are
  const parts = labelledParts(text);
  if (parts) {
    const inner = readWorkRequest(parts.rest);
    if (inner && inner.kind === "notice") {
      const { message: saidAfter, aside, ...request } = inner;
      const message = parts.message ?? saidAfter;
      return { ...request, title: parts.title, ...(message ? { message } : {}), ...(aside ? { aside } : {}) };
    }
  }
  // "I'd like to ask parents which fundraiser they prefer: cake sale, raffle or fun run": a Poll, with its choices
  const asksWhich = text.match(ASK_THE_PARENTS_WHICH);
  if (asksWhich) {
    const asked = `${asksWhich[1]} ${asksWhich[2]}`.replace(/\s+they\s+(\w+)/i, " do you $1").trim();
    const question = `${asked.charAt(0).toUpperCase()}${asked.slice(1)}?`;
    return { kind: "poll", request: `create a poll: ${question} ${asksWhich[3].trim()}`, meansTheOpenOne: false, atStart: false, sentence: 0 };
  }
  const pollAsked = text.match(ASK_THE_PARENTS);
  if (pollAsked) {
    return { kind: "poll", request: `create a poll asking ${pollAsked[1].trim()}`, meansTheOpenOne: false, atStart: false, sentence: 0 };
  }
  // "I want to ask all parents should we go to the zoo or the aquarium?", "…, Yes or No"
  const askedAny = text.match(ASK_THE_PARENTS_ANY);
  if (askedAny) {
    return { kind: "poll", request: `create a poll: ${questionThenChoices(askedAny[1])}`, meansTheOpenOne: false, atStart: false, sentence: 0 };
  }
  const invited = text.match(INVITE_TO_SOMETHING);
  if (invited) {
    const what = invited[1].replace(/^(?:something|a\s+thing|an?\s+event|stuff)\b\s*/i, "").trim();
    return { kind: "event", request: what ? `create an event called ${what}` : "create an event", meansTheOpenOne: false, atStart: true, sentence: 0 };
  }
  if (RUN_A_VOTE.test(text)) return { kind: "poll", request: "create a poll", meansTheOpenOne: false, atStart: true, sentence: 0 };
  if (ON_THE_CALENDAR.test(text)) return { kind: "event", request: "create an event", meansTheOpenOne: false, atStart: true, sentence: 0 };
  const additional = text.match(/^(?:(?:oh|and|also)[,\s]+)*i\s+(?:also\s+)?(?:need|want)\s+(?:another|a\s+new|a|an)\s+(notice|poll|event)[.!?]*$/i);
  if (additional) return { kind: kindOf(additional[1]), request: `create a ${additional[1]}`, meansTheOpenOne: false, atStart: true, sentence: 0 };
  const helpMake = text.match(HELP_ME_MAKE_ONE);
  if (helpMake) {
    const kind = kindOf(helpMake[1]);
    const request = text.replace(/^.*?help\s+me\s+\w+(?:\s+together)?\s+/i, "create ").replace(/[\s?.!]+$/, "");
    return { kind, request, meansTheOpenOne: false, atStart: true, sentence: 0 };
  }
  // "i want to send grade 4 parents a note about the swimming gala": who it's for, then the Notice
  const sendThem = text.match(SEND_THEM_A_NOTE);
  if (sendThem) {
    const supplied = sendThem[2]?.match(/^that\s+(.+)$/i)?.[1];
    if (supplied) return { kind: "notice", request: `create a notice to ${sendThem[1].trim()}`, message: supplied, meansTheOpenOne: false, atStart: true, sentence: 0 };
    const about = sendThem[2]?.replace(/[\s.!]+$/, "").trim();
    return { kind: "notice", request: `create a notice to ${sendThem[1].trim()}${about ? ` ${about}` : ""}`, meansTheOpenOne: false, atStart: true, sentence: 0 };
  }
  // "I want to send something to Grade 4 parents", "I need to tell all parents about term reports"
  const tell = text.match(TELL_SOMEONE);
  if (tell && !new RegExp(`\\b${NOUN}\\b`, "i").test(tell[1]) && !looksLikeQuestion(tell[1])) {
    const rest = tell[1]
      .replace(/^(.+?)\s+know\b/i, "$1")
      .replace(NO_TOPIC, "")
      .replace(/[\s.!]+$/, "")
      .trim();
    const to = /^(?:to|about)\b/i.test(rest) ? rest : `to ${rest}`;
    return { kind: "notice", request: `create a notice${rest ? ` ${to}` : ""}`, meansTheOpenOne: false, atStart: true, sentence: 0 };
  }
  // "whole school notice about thank you"
  const audienceFirst = text.match(AUDIENCE_THEN_NOUN);
  if (audienceFirst) {
    const kind = kindOf(audienceFirst[2]);
    const rest = audienceFirst[3].replace(/^(?:please|pls|plz)[.!?]*$/i, "").replace(NO_TOPIC, "").replace(/[\s.!]+$/, "").trim();
    const an = kind === "event" ? "an" : "a";
    return { kind, request: `create ${an} ${kind} for ${audienceFirst[1]}${rest ? ` ${rest}` : ""}`, meansTheOpenOne: false, atStart: true, sentence: 0 };
  }
  const sentences = sentencesOf(text);
  // "Maths evening for the grade 4 parents, tomorrow 6pm, the library. can you add it?": an Event, described first
  const last = sentences.length > 1 ? sentences[sentences.length - 1] : undefined;
  if (last && ADD_IT.test(last.text.trim())) {
    const described = text.slice(0, last.start).trim().replace(/[.!]+$/, "");
    if (SAYS_WHEN.test(described) && !new RegExp(`\\b${NOUN}\\b`, "i").test(described)) {
      return { kind: "event", request: `create an event called ${described}`, meansTheOpenOne: false, atStart: false, sentence: sentences.length - 1 };
    }
  }
  for (let index = 0; index < sentences.length; index += 1) {
    const sentence = sentences[index].text;
    const opened = sentence.replace(/^\s+/, "").replace(OPENER, "").replace(SET_ASIDE_FIRST, "").replace(OPENER, "");
    const lead = sentence.length - opened.length;
    // "can I make a poll", "can you make the image notice": asked, but asking for it to be made
    const politeAsk = /^(?:can|could|would|will)\s+(?:i|you|we)\b/i.test(opened);
    if (ASKS_ABOUT.test(opened) && !politeAsk) continue;
    // "can I send a notice to just one parent?": asking whether it can be done, which is answered first
    if (/^can\s+i\b/i.test(opened) && ONE_PERSON.test(opened)) continue;
    if (NOT_A_REQUEST.test(opened)) continue;

    const pollWithChoices = opened.match(POLL_WITH_CHOICES);
    if (pollWithChoices) {
      const about = pollWithChoices[1].trim();
      const choices = [pollWithChoices[2], ...sentences.slice(index + 1).map((s) => s.text)].join(" ").trim();
      return {
        kind: "poll",
        request: `create a poll${about ? ` for the ${about}` : ""}: ${choices}`,
        meansTheOpenOne: false,
        atStart: true,
        sentence: index,
      };
    }

    const first = opened.match(NOUN_FIRST);
    const verbed = first ? null : opened.match(VERB_THEN_NOUN);
    const found = first ?? verbed;
    if (!found || found.index === undefined) continue;
    if (/\bhow (?:do|can|would) i\b/i.test(opened.slice(0, found.index))) continue;
    // "events next weekend?", "notices for grade 4": several of them, looked up, never one to make
    if (first && /\b(?:notices|announcements|events|polls)\s*$/i.test(found[0])) continue;
    // "the event", "my notice" on their own point at one, they don't ask for one
    if (first && meansOne(article(found[1])) && !opened.slice(found[0].length).replace(/^[\s.!:,;–—-]+/, "").trim()) continue;
    // "can I make a poll anonymous?": asking what a Poll can be, not for one to be made
    if (politeAsk && /^(?:i|we)\b/i.test(opened.replace(/^(?:can|could|would|will)\s+/i, "")) && WHAT_IT_CAN_BE.test(opened.slice(found.index + found[0].length))) continue;
    const noun = found[first ? 2 : 4];
    const said = first ? article(found[1]) : article(found[2]);
    const kind = kindOf(noun);
    const atRequest = sentences[index].start + lead + found.index;
    const atStart = (found.index === 0 || /^(?:(?:can|could|would|will)\s+(?:i|we|you)|i\s+(?:want|need|would like)\s+to)\s*$/i.test(opened.slice(0, found.index))) && sentences.slice(0, index).every((s) => s.text.trim().split(/\s+/).length <= 6);
    if (kind === "event" && /\b(?:place|venue|location|day|date|time)\s*:/i.test(text.slice(atRequest))) {
      return { kind, request: text.slice(atRequest), meansTheOpenOne: meansOne(said), atStart, sentence: index };
    }
    if (kind === "notice" && /\b(?:saying|(?:message|body|text)\s*:|that\s+says\b)/i.test(text.slice(atRequest)) && !text.slice(sentences[index].start + sentence.length).match(ANOTHER_REQUEST)) {
      return { kind, request: text.slice(atRequest), meansTheOpenOne: meansOne(said), atStart, sentence: index };
    }
    const request = text.slice(atRequest, sentences[index].start + sentence.length).trim();

    if (kind === "notice" && (PICTURE.test(sentence) || /\btype\s+image\b/i.test(text))) {
      return { kind: "image", request };
    }
    if (first) {
      // "event. staff meeting next wednesday…", "POLL TIME!!! Which house will win?": what follows says what it is
      const named = opened.slice(found[0].length).replace(/^[\s.!:,;–—-]+/, "").trim();
      const later = sentences.slice(index + 1).map((s) => s.text.trim());
      // A Poll takes everything after it (its question, then its choices); a Notice or Event the
      // rest of this sentence, or the next one when this one is just "event."
      const [body, usedUpTo] =
        kind === "poll"
          ? [[named, ...later].join(" ").trim(), sentences.length]
          : named
            ? [named, index + 1]
            : [later[0] ?? "", index + 2];
      const describing = found[0]
        .replace(new RegExp(`${NOUN}(?:\\s+time)?$`, "i"), "")
        .replace(new RegExp(`^(?:${ARTICLE})\\s+`, "i"), "")
        .trim();
      const what = `${describing ? `${describing.toLowerCase()} ` : ""}${kind}`;
      const an = /^[aeiou]/i.test(what) ? "an" : "a";
      // "an event for the spirit week dance…", "an info notice to Grade 3: …": already joined to what follows
      const joined = /^(?:for|to|about|called|named|on|asking)\b/i.test(body);
      // "Notice: Bus route 3 changes from Monday, it now goes via Oak Avenue…": a Notice's message, not its title
      const longNotice = kind === "notice" && !joined && body.split(/\s+/).length > 8;
      const { to: saidTo, ...rest } = kind === "poll" ? NOTHING_AFTER : after(sentences, usedUpTo);
      const asked = !body || longNotice ? `create ${an} ${what}` : joined ? `create ${an} ${what} ${body}` : `create ${an} ${what}: ${body}`;
      return {
        kind,
        request: saidTo ? toAfterTheNoun(asked, saidTo) : asked,
        ...rest,
        ...(longNotice ? { message: [body, "message" in rest ? rest.message : ""].filter(Boolean).join(" ") } : {}),
        meansTheOpenOne: meansOne(said),
        sentence: index,
        // "Notice: …" labels what follows as the request
        atStart: atStart || (found.index === 0 && /^\s*:/.test(opened.slice(found[0].length))),
      };
    }
    // "Now, the bake sale itself. … It's on Saturday 7 November. Can you put that in as an event?": made from what came before
    if (BACK_REFERENCE.test(found[1]) && index > 0 && kind === "poll") {
      const question = sentences.slice(0, index).map((s) => s.text.trim()).join(" ");
      const audience = opened.slice(found.index + found[0].length).trim();
      return { kind, request: `create a poll ${audience} asking ${question}`, meansTheOpenOne: false, atStart, sentence: index };
    }
    if (BACK_REFERENCE.test(found[1]) && index > 0 && kind !== "poll") {
      const earlier = sentences
        .slice(0, index)
        .map((s) => s.text.trim().replace(OPENER, "").trim())
        .filter(Boolean);
      const named = earlier.findIndex((s) => NAMED_ON_ITS_OWN.test(s));
      const title = named >= 0 ? capitalized(earlier[named].match(NAMED_ON_ITS_OWN)?.[1] ?? "") : undefined;
      const message = (named >= 0 ? earlier.slice(named + 1) : earlier).join(" ").trim();
      return {
        kind,
        request: `create ${kind === "event" ? "an" : "a"} ${kind}`,
        ...(message ? { message } : {}),
        ...(title ? { title } : {}),
        meansTheOpenOne: false,
        atStart: false,
        sentence: index,
      };
    }
    // "a notice about the bus route change, an event for the science fair on the 5th, and a poll…"
    const nextOne = Array.from(request.matchAll(ANOTHER_REQUEST)).find((m) => (m.index ?? 0) > found[0].length);
    const thenAlso = nextOne
      ? Array.from(request.slice(nextOne.index).matchAll(ANOTHER_REQUEST)).map((m) => kindOf(m[1]))
      : [];
    const ownRequest = nextOne ? request.slice(0, nextOne.index) : request;
    // A Poll's question and choices can run over several sentences: "…next read. The title must be: X? Options: …"
    const pollRequest = kind === "poll" && !nextOne ? text.slice(atRequest).trim() : ownRequest;
    // "make the science fair event": called Science fair
    const descriptor = (found[3] ?? "").trim();
    // "create a swimming lessons event every Friday": called Swimming lessons too, unless it says who it's for
    const describesIt = descriptor && !DESCRIBING_ONLY.test(`${descriptor} `) && !SAYS_WHO.test(descriptor);
    const title = describesIt && (meansOne(said) || ["a", "an"].includes(said)) ? capitalized(descriptor) : undefined;
    const { to: saidTo, ...afterwards } = kind === "poll" ? NOTHING_AFTER : after(sentences, index + 1);
    return {
      kind,
      request: saidTo ? toAfterTheNoun(pollRequest, saidTo) : pollRequest,
      ...afterwards,
      meansTheOpenOne: meansOne(said),
      atStart,
      sentence: index,
      ...(title && kind !== "poll" ? { title } : {}),
      ...(thenAlso.length ? { thenAlso } : {}),
    };
  }
  // "Hi, sorry, I'm new. How do I send a notice? Can you do it with me?": the kind named before, made together
  if (last && DO_IT_WITH_ME.test(last.text.trim())) {
    const named = text.slice(0, last.start).match(new RegExp(`\\b${NOUN}\\b`, "gi"));
    if (named) {
      const kind = kindOf(named[named.length - 1]);
      return { kind, request: `create ${kind === "event" ? "an" : "a"} ${kind}`, meansTheOpenOne: false, atStart: false, sentence: sentences.length - 1 };
    }
  }
  return null;
}

/**
 * "can you make the image notice for me?", "just do it for me", "cant u make the img notis 4 me?":
 * said right after an image notice was explained, it asks Ask to make that one.
 */
export function asksToMakeItForThem(said: string): boolean {
  const text = withoutTextSpeak(said.trim());
  return (
    /\b(?:you|ya)\b[^.?!]*\b(?:make|do|create|send|post)\b/i.test(text) ||
    /\b(?:do|make)\s+it\s+for\s+me\b|\bjust\s+do\s+it\b|\bfor\s+me\b/i.test(text)
  );
}

/** "the pic one", "img notis", "image": an image notice, named where a kind of notice was asked for. */
export function namesAPicture(said: string): boolean {
  return PICTURE.test(withoutTextSpeak(said));
}
